import { useEffect, useState } from 'react';
import {
  Calendar,
  Clock,
  Cog,
  DollarSign,
  Download,
  GraduationCap,
  MapPin,
  Piano,
  Shirt,
} from 'lucide-react';
import { PhoneFrame } from './PhoneFrame';
import { prefersReducedMotion, useInView } from './useInView';
import {
  AppHeader,
  AppScreen,
  BalanceCard,
  CalendarGrid,
  CalendarLegend,
  ExpenseRow,
  Fab,
  MonthNav,
  StatusBar,
} from './kit';
import type { CalendarDay, CalendarDayFill } from './kit';
import './HeroMotionDemo.css';

type Phase =
  | 'pre'
  | 'reveal'
  | 'adding'
  | 'pending'
  | 'approved'
  | 'hold'
  | 'final';

const FEB_FILLS: Record<number, CalendarDayFill> = {
  1: 'them',
  2: 'them',
  3: 'them',
  4: 'them',
  5: 'them',
  6: 'you',
  7: 'you',
  9: 'them',
  10: 'them',
  11: 'them',
  12: 'them',
  13: 'you',
  14: 'them',
  15: 'them',
  16: 'them',
  17: 'them',
  18: 'them',
  19: 'them',
  20: 'you',
  21: 'them',
  22: 'them',
  23: 'them',
  24: 'them',
  25: 'them',
  26: 'them',
  27: 'them',
  28: 'them',
};

const BASE_AMOUNT = 75;
const NEW_AMOUNT = 97.5;

function buildDays(swapApproved: boolean, pendingDot: boolean): CalendarDay[] {
  return Array.from({ length: 28 }, (_, index) => {
    const day = index + 1;

    if (day === 8) {
      return { day, selected: true };
    }

    if (day === 22) {
      return {
        day,
        fill: swapApproved ? 'you' : 'them',
        pending: pendingDot,
      };
    }

    return { day, fill: FEB_FILLS[day] };
  });
}

export function HeroMotionDemo() {
  const reduced = prefersReducedMotion();
  const { ref, seen } = useInView<HTMLDivElement>({ threshold: 0.2 });
  const [phase, setPhase] = useState<Phase>(reduced ? 'final' : 'pre');
  const [balance, setBalance] = useState(reduced ? NEW_AMOUNT : BASE_AMOUNT);

  useEffect(() => {
    if (reduced) {
      setPhase('final');
      return;
    }
    if (!seen) return;

    setPhase('reveal');

    const timers = [
      window.setTimeout(() => setPhase('adding'), 1800),
      window.setTimeout(() => setPhase('pending'), 2100),
      window.setTimeout(() => setPhase('approved'), 3500),
      window.setTimeout(() => setPhase('hold'), 4700),
      window.setTimeout(() => setPhase('final'), 5600),
    ];

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [reduced, seen]);

  const isFinal = reduced || phase === 'final';
  const rowIn =
    isFinal ||
    phase === 'adding' ||
    phase === 'pending' ||
    phase === 'approved' ||
    phase === 'hold';
  const swapApproved = isFinal || phase === 'approved' || phase === 'hold';
  const pendingDot = phase === 'pending' || phase === 'approved';
  const dotFading = phase === 'approved';
  const phonesVisible = isFinal || phase !== 'pre';

  useEffect(() => {
    if (isFinal) {
      setBalance(NEW_AMOUNT);
      return;
    }

    if (!rowIn) {
      setBalance(BASE_AMOUNT);
      return;
    }

    let frame = 0;
    const startedAt = performance.now();

    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / 1200);
      const eased = 1 - Math.pow(1 - progress, 3);
      setBalance(BASE_AMOUNT + (NEW_AMOUNT - BASE_AMOUNT) * eased);
      if (progress < 1) {
        frame = window.requestAnimationFrame(tick);
      }
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [isFinal, rowIn]);

  const days = buildDays(swapApproved, pendingDot);
  const amount = `$${balance.toFixed(2)}`;

  return (
    <div ref={ref} className={`hero-demo hero-demo--${phase}`}>
      <div className='hero-demo__stage'>
        <div
          className={`hero-demo__phone hero-demo__phone--left${
            phonesVisible ? ' is-visible' : ''
          }`}
        >
          <PhoneFrame size='lg' glow>
            <AppScreen size='lg'>
              <StatusBar time='3:50' inset battery='full' />
              <AppHeader variant='title' title='Calendar' />
              <div className='mk-body hero-cal-body'>
                <CalendarLegend themLabel='Jake has kids' />
                <MonthNav month='February 2026' />
                <div
                  className={`hero-cal${dotFading ? ' is-dot-fading' : ''}`}
                >
                  <CalendarGrid weeks={days} />
                </div>
                <p className='hero-label'>Schedules</p>
                <div className='hero-schedules'>
                  <div className='hero-schedule-card'>
                    <span className='hero-schedule-icon'>
                      <MapPin size={18} strokeWidth={2.2} />
                    </span>
                    <span className='hero-schedule-body'>
                      <span className='hero-schedule-title'>Check-ins</span>
                      <span className='hero-schedule-sub'>GPS Locations</span>
                    </span>
                  </div>
                  <div className='hero-schedule-card'>
                    <span className='hero-schedule-icon'>
                      <Calendar size={18} strokeWidth={2.2} />
                    </span>
                    <span className='hero-schedule-body'>
                      <span className='hero-schedule-title'>Bobby</span>
                      <span className='hero-schedule-sub'>
                        Weekdays + Alternating
                      </span>
                    </span>
                  </div>
                </div>
                <div className='hero-date-card mk-card'>
                  <p className='hero-date-card__title'>Sunday, February 8</p>
                  <p className='hero-date-card__name'>Bobby</p>
                  <div className='hero-date-card__schedule'>
                    <p className='hero-date-card__meta'>
                      <Calendar size={14} strokeWidth={2.2} />
                      Weekdays + Alternating Weekends
                    </p>
                    <span className='hero-date-card__you'>You</span>
                  </div>
                  <p className='hero-date-card__meta hero-date-card__meta--muted'>
                    <Clock size={14} strokeWidth={2.2} />
                    Drop off at 7:00 PM
                  </p>
                </div>
              </div>
              <Fab />
            </AppScreen>
          </PhoneFrame>
        </div>

        <div
          className={`hero-demo__phone hero-demo__phone--right${
            phonesVisible ? ' is-visible' : ''
          }`}
        >
          <PhoneFrame size='lg' glow>
            <AppScreen size='lg'>
              <StatusBar time='6:57' inset battery='full' />
              <AppHeader
                variant='brand'
                title='Harbor'
                rightIcon={Download}
              />
              <div className='mk-body hero-home-body'>
                <BalanceCard
                  label='Amount Owed'
                  amount={amount}
                  leftLabel='New Expense'
                  rightLabel='New Payment'
                />
                <h3 className='hero-home__section'>All transactions</h3>
                <div className='hero-home__list'>
                  {rowIn ? (
                    <div className='hero-home__row'>
                      <ExpenseRow
                        icon={Shirt}
                        iconColor='#018B9C'
                        title='Soccer Jersey'
                        sub='Clothing'
                        amount='$45.00'
                        split='50% - 50%'
                      />
                    </div>
                  ) : null}
                  <ExpenseRow
                    icon={DollarSign}
                    iconColor='#3B82F6'
                    title='Payment'
                    sub='Jan 1'
                    amount='$25.00'
                  />
                  <ExpenseRow
                    icon={GraduationCap}
                    iconColor='#5B8DEF'
                    title='Calculator'
                    sub='School · Jan 1'
                    amount='$150.00'
                    split='50% - 50%'
                  />
                  <ExpenseRow
                    icon={Piano}
                    iconColor='#F5A623'
                    title='Piano for bobby'
                    sub='Entertainment · Dec 23 🎉'
                    amount='$300.00'
                    split='50% - 50%'
                  />
                  <ExpenseRow
                    icon={Cog}
                    iconColor='#4EC9A0'
                    title='Piano lessons'
                    sub='Activities · Dec 22 🎉'
                    amount='$50.00'
                    split='50% - 50%'
                  />
                </div>
              </div>
              <Fab />
            </AppScreen>
          </PhoneFrame>
        </div>
      </div>
    </div>
  );
}
